import React, { useEffect, useState } from 'react';
import { Table, message } from 'antd';
import { getReportApiByKey } from '@app/api/reports.api';

interface HReportRow {
  key: string | number;

  // Left columns
  date?: string;
  invoice_generated_by?: string;
  invoice_number?: string;
  code?: string;
  invoice_generated_for?: string;
  amount?: number;
  total_selected_amount?: number;

  // Right columns
  rightDate?: string;
  disbursements?: string;
  rightAmount?: number;
  rightTotal?: number;
}

interface HReportProps {
  reportKey: string;
  filters: any;
}

const HReport: React.FC<HReportProps> = ({ reportKey, filters }) => {
  const [data, setData] = useState<HReportRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!reportKey || !filters) return;

      const api = getReportApiByKey(reportKey);
      if (!api) {
        message.error('Invalid report key.');
        return;
      }

      setLoading(true);
      try {
        const response = await api.generateReport(filters);

        const rows: HReportRow[] = (response?.report || []).map((item: any, index: number) => ({
          key: index,

          created_at: item.created_at,
          invoice_generated_by: item.invoice_generated_by,
          code: item.code,
          invoice_generated_for: item.invoice_generated_for,
          invoice_number: item.invoice_number,
          amount: item.amount,
          total_selected_amount: item.total_selected_amount,

          rightDate: item.right_date,
          disbursements: item.disbursements,
          rightAmount: item.right_amount,
          rightTotal: item.right_total,
        }));

        setData(rows);
      } catch (err) {
        console.error(err);
        message.error('Failed to fetch H Report data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [reportKey, filters]);

  const columns: any[] = [
    // LEFT
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
    },
    {
      title: 'Invoice Generated By',
      dataIndex: 'invoice_generated_by',
      key: 'invoice_generated_by',
    },
    {
      title: 'Invoice',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
    },
    {
      title: 'Receipt',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: 'Invoice Generated for',
      dataIndex: 'invoice_generated_for',
      key: 'invoice_generated_for',
    },
    {
      title: 'Amount (Rs.)',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (v: number) => (v !== undefined ? v.toLocaleString('en-LK', { minimumFractionDigits: 2 }) : '-'),
    },
    {
      title: 'Total (Rs.)',
      dataIndex: 'total_selected_amount',
      key: 'total_selected_amount',
      align: 'right',
      className: 'h-report-split-border',
      render: (v: number) => (v !== undefined ? v.toLocaleString('en-LK', { minimumFractionDigits: 2 }) : '-'),
    },

    // RIGHT
    {
      title: 'Date',
      dataIndex: 'rightDate',
      key: 'rightDate',
    },
    {
      title: 'Disbursements',
      dataIndex: 'disbursements',
      key: 'disbursements',
    },
    {
      title: 'Amount (Rs.)',
      dataIndex: 'rightAmount',
      key: 'rightAmount',
      align: 'right',
      render: (v: number) => (v !== undefined ? v.toLocaleString('en-LK', { minimumFractionDigits: 2 }) : ''),
    },
    {
      title: 'Total (Rs.)',
      dataIndex: 'rightTotal',
      key: 'rightTotal',
      align: 'right',
      render: (v: number) => (v !== undefined ? v.toLocaleString('en-LK', { minimumFractionDigits: 2 }) : ''),
    },
  ];

  return (
    <>
      {/* Divider styling */}
      <style>
        {`
    .h-report-split-border {
      border-right: 3px solid #595959 !important;
    }
  `}
      </style>

      <Table columns={columns} dataSource={data} bordered pagination={false} loading={loading} />
    </>
  );
};

export default HReport;
